/**
 * /inbox — full broadcast history for the current user.
 */
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListRowSkeleton } from "@/components/common/Skeletons";
import EmptyState from "@/components/common/EmptyState";
import { Bell } from "lucide-react";
import {
  useMyBroadcasts,
  useMarkBroadcastRead,
} from "@/lib/broadcasts/useBroadcasts";

const InboxInner = () => {
  const { data, isLoading } = useMyBroadcasts();
  const markRead = useMarkBroadcastRead();

  if (isLoading) {
    return <ListRowSkeleton rows={4} />;
  }

  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={Bell}
        title="No announcements yet"
        description="We'll let you know when there's news from your community."
        ctaLabel="Explore Discover"
        ctaHref="/discover"
      />
    );
  }

  return (
    <ul className="space-y-3">
      {data.map((b) => {
        const unread = !b.receipt?.read_at && !b.receipt?.dismissed_at;
        return (
          <li
            key={b.id}
            className={`rounded-2xl border border-border p-4 ${
              unread ? "bg-card" : "bg-background"
            }`}
            onClick={() => unread && markRead.mutate(b.id)}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold truncate">{b.title}</h3>
                  {unread && <Badge variant="default">New</Badge>}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(b.created_at).toLocaleString()}
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm whitespace-pre-wrap">{b.body}</p>
            {b.cta_url && b.cta_label ? (
              <div className="mt-4">
                <Button asChild size="sm">
                  <a
                    href={b.cta_url}
                    target={b.cta_url.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                  >
                    {b.cta_label}
                  </a>
                </Button>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

const Inbox = () => (
  <div className="min-h-screen flex flex-col bg-background text-foreground">
    <SiteHeader />
    <OnboardingGate>
      <main className="mx-auto w-full max-w-3xl px-6 py-10">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Wavlynq
          </p>
          <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">
            Announcements
          </h1>
        </header>
        <InboxInner />
      </main>
    </OnboardingGate>
    <SiteFooter />
  </div>
);

export default Inbox;
